import type { Segment, SegmentId } from '@hotjar/webapp/features/Recordings/types';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import type { SiteId } from '@hotjar/site';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import type { ApiBaseResponse } from '@hotjar/webapp/domains/common/api';
import { SEGMENTS_QUERY_KEY, LEGACY_SEGMENT_TYPE } from './constant';

const API_ROOT = '/api/v1/sites';

function createEndpointPath(siteId: SiteId) {
  return `${API_ROOT}/${siteId}/segments`;
}

interface NewSegmentData extends ApiBaseResponse {
  // eslint-disable-next-line camelcase
  segment_id: SegmentId;
}

export interface getAllResponseDTO {
  segments: Omit<Segment, 'icon'>[];
}

export function update(
  siteId: SiteId,
  segmentId: SegmentId,
  segment: Segment,
): Promise<AxiosResponse<ApiBaseResponse>> {
  // Even if it's not used in the frontend
  // 'applies_to' prop is needed for the backend to work
  const segmentToUpdate = {
    ...segment,
    applies_to: LEGACY_SEGMENT_TYPE,
  };
  const url = `${createEndpointPath(siteId)}/${segmentId}`;
  queryClient.invalidateQueries(`${SEGMENTS_QUERY_KEY}_${siteId}`);

  return axios.put(url, segmentToUpdate);
}

export function create(
  siteId: SiteId,
  segment: Omit<Segment, 'id'>,
): Promise<AxiosResponse<NewSegmentData>> {
  // Even if it's not used in the frontend
  // 'applies_to' prop is needed for the backend to work
  const segmentToCreate = {
    ...segment,
    applies_to: LEGACY_SEGMENT_TYPE,
  };
  const url = createEndpointPath(siteId);
  queryClient.invalidateQueries(`${SEGMENTS_QUERY_KEY}_${siteId}`);

  return axios.post(url, segmentToCreate);
}

export async function getAll(siteId: SiteId): Promise<Segment[]> {
  const url = createEndpointPath(siteId);
  const response = await axios.get<getAllResponseDTO>(url, {
    params: { applies_to: LEGACY_SEGMENT_TYPE },
  });
  return response.data.segments;
}

export async function remove(siteId: SiteId, segmentId: SegmentId): Promise<SegmentId> {
  const url = `${createEndpointPath(siteId)}/${segmentId}`;
  const response = await axios.delete<ApiBaseResponse>(url);
  if (!response.data?.success) {
    throw response.data.error;
  }

  queryClient.invalidateQueries(`${SEGMENTS_QUERY_KEY}_${siteId}`);
  return segmentId;
}
